import { ReactNode } from "react";
import { AxiosError } from "axios";
import { Alert, Colors, Icon } from "swing-components";
import { generateSwingSupportURL } from "swing-utils";

import { AlertError, ButtonScore } from "~components";
import { RequestDetailsErrorResponse, translate } from "~utils";

import "./ModalConfirmation.css"; // Note .css as ionic overrides needed

type ModalConfirmationProps = {
  children: ReactNode;
  onDismiss: (data?: string | null | undefined | number, role?: string) => void;
  onConfirm: () => void;
  title: string;
  error?: AxiosError | null;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  confirmDisabled?: boolean;
};

// Note: controlled by `useModal` hook via useIonModal `hook`.
export function ModalConfirmation(props: ModalConfirmationProps) {
  const {
    children,
    onDismiss,
    onConfirm,
    title,
    error = null,
    primaryButtonText = "Yes",
    secondaryButtonText = "No",
    confirmDisabled = false,
  } = props;

  const errResponseData = error?.response?.data as RequestDetailsErrorResponse;

  return (
    <div className="modal-wrapper">
      <div className="modal-header">
        <h2>{title}</h2>
        {/* FIXME: when design system button is available */}
        <button onClick={() => onDismiss()} css={{ backgroundColor: "transparent" }}>
          <Icon name="Close" color={Colors.blue500} />
        </button>
      </div>
      <div className="modal-body">
        {errResponseData &&
          (errResponseData?.detail?.exception === "request-unavailable" ? (
            <div css={{ margin: "16px 0px" }}>
              <Alert color="warning" title={translate("UNAVAILABLE_REQUEST_ALERT_TITLE")} showIcon>
                {translate("LABEL_UNAVAILABLE_REQUEST")}
              </Alert>
            </div>
          ) : errResponseData?.detail?.exception === "preferred-sub-with-conflict" ? (
            <div css={{ margin: "16px 0px" }}>
              <Alert color="warning" title={translate("SUB_CONFLICT_ALERT_TITLE")} showIcon>
                <p
                  dangerouslySetInnerHTML={{
                    __html: translate("SUB_CONFLICT_ALERT_MSG", {
                      supportLink: generateSwingSupportURL("/hc/en-us/requests/new"),
                    }),
                  }}
                />
              </Alert>
            </div>
          ) : errResponseData.error ? (
            <AlertError />
          ) : null)}

        {children}
      </div>
      <div className="modal-footer">
        <div className="modal-footer-button-spacing">
          <ButtonScore
            label={primaryButtonText}
            onClick={onConfirm}
            disabled={
              confirmDisabled || errResponseData?.detail?.exception === "request-unavailable"
            }
          />
        </div>
        <ButtonScore label={secondaryButtonText} fill="outline" onClick={onDismiss} />
      </div>
    </div>
  );
}
